import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { 
  FileText, 
  Download, 
  Copy, 
  Edit3, 
  Save, 
  Loader2, 
  LogOut,
  Calendar,
  Clock
} from "lucide-react";
import Letterhead from "@/components/Letterhead";
import SpeechText from "@/components/SpeechText";
import html2pdf from "html2pdf.js";

const API = process.env.REACT_APP_BACKEND_URL;

const MySpeechesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [speeches, setSpeeches] = useState([]);
  const [email, setEmail] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedContent, setEditedContent] = useState({});
  const [activeDraft, setActiveDraft] = useState({});

  const token = localStorage.getItem("mf_token");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchSpeeches();
  }, [token, navigate]);

  const fetchSpeeches = async () => {
    try {
      const res = await axios.get(`${API}/api/auth/my-speeches?token=${token}`);
      setSpeeches(res.data.speeches);
      setEmail(res.data.email);
    } catch {
      localStorage.removeItem("mf_token");
      localStorage.removeItem("mf_email");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("mf_token");
    localStorage.removeItem("mf_email");
    navigate("/");
  };

  const copyToClipboard = (text) => {
    // Strip [Note: ...] for clipboard
    const clean = text.replace(/\[Note:\s*[^\]]+\]/g, "").replace(/\n{3,}/g, "\n\n");
    navigator.clipboard.writeText(clean);
    toast.success("Kopieret til udklipsholder!");
  };

  const downloadAsPDF = (elementId, recipientName) => {
    const element = document.getElementById(elementId);
    if (!element) return;
    const safeName = recipientName.replace(/[^a-zA-Z0-9æøåÆØÅ]/g, "_");
    const filename = `festtale_${safeName}.pdf`;
    html2pdf().set({
      margin: [10, 10, 10, 10],
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
    }).from(element).save();
    toast.success("PDF hentes...");
  };

  const getDaysLeft = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 md:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">
              Mine taler
            </h1>
            <p className="text-muted-foreground mt-1">{email}</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Log ud
          </Button>
        </div>

        {speeches.length === 0 ? (
          <Card className="border-border/40">
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold text-primary mb-2">
                Ingen taler endnu
              </h2>
              <p className="text-muted-foreground mb-6">
                Dine købte taler vises her. Adgang i 90 dage efter køb.
              </p>
              <Link to="/tale">
                <Button className="bg-primary hover:bg-primary/90 rounded-full">
                  Skriv din første tale
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {speeches.map((speech) => {
              const hasDraft2 = !!speech.full_content_draft2;
              const currentDraft = activeDraft[speech.id] || 1;
              const currentContent = currentDraft === 2 && hasDraft2
                ? (editedContent[`${speech.id}_d2`] ?? speech.full_content_draft2)
                : (editedContent[speech.id] ?? speech.full_content);
              const editKey = currentDraft === 2 ? `${speech.id}_d2` : speech.id;
              const originalForDraft = currentDraft === 2 && hasDraft2
                ? speech.full_content_draft2
                : speech.full_content;
              const daysLeft = getDaysLeft(speech.access_expires);

              return (
                <Card key={speech.id} className="border-border/40 overflow-hidden" data-testid={`my-speech-${speech.id}`}>
                  <CardContent className="p-0">
                    {/* Speech header */}
                    <div className="p-5 md:p-6 border-b border-border/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <h2 className="font-heading text-lg font-bold text-primary">
                            {speech.speech_title || "Festtale"}
                          </h2>
                          <p className="text-sm text-muted-foreground">
                            Til {speech.recipient_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(speech.created_at).toLocaleDateString("da-DK")}
                        </span>
                        <span className="flex items-center gap-1 bg-muted px-2 py-1 rounded-full text-xs">
                          <Clock className="h-3 w-3" />
                          {daysLeft} dage tilbage
                        </span>
                      </div>
                    </div>

                    {/* Draft tabs + Actions */}
                    <div className="p-5 md:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                        {/* Draft tabs */}
                        {hasDraft2 && (
                          <div className="flex gap-2" data-testid="my-speech-draft-tabs">
                            <button
                              onClick={() => { setActiveDraft(prev => ({ ...prev, [speech.id]: 1 })); setEditingId(null); }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                currentDraft === 1 ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                              }`}
                            >
                              Udkast 1
                            </button>
                            <button
                              onClick={() => { setActiveDraft(prev => ({ ...prev, [speech.id]: 2 })); setEditingId(null); }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                currentDraft === 2 ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                              }`}
                            >
                              Udkast 2
                            </button>
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex gap-2">
                          {editingId !== editKey ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingId(editKey);
                                  if (!editedContent[editKey]) {
                                    setEditedContent(prev => ({ ...prev, [editKey]: originalForDraft }));
                                  }
                                }}
                                data-testid={`my-edit-btn-${speech.id}`}
                              >
                                <Edit3 className="h-3.5 w-3.5 mr-1.5" />
                                Rediger
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyToClipboard(currentContent)}
                              >
                                <Copy className="h-3.5 w-3.5 mr-1.5" />
                                Kopiér
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => downloadAsPDF(`my-letterhead-${speech.id}`, speech.recipient_name)}
                                className="bg-primary hover:bg-primary/90"
                                data-testid={`my-download-btn-${speech.id}`}
                              >
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                                PDF
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditedContent(prev => ({ ...prev, [editKey]: originalForDraft }));
                                  setEditingId(null);
                                }}
                              >
                                Annuller
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => { setEditingId(null); toast.success("Ændringer gemt!"); }}
                                className="bg-accent hover:bg-accent/90"
                              >
                                <Save className="h-3.5 w-3.5 mr-1.5" />
                                Gem
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Content */}
                      {editingId === editKey ? (
                        <Textarea
                          value={editedContent[editKey] || currentContent}
                          onChange={(e) => setEditedContent(prev => ({ ...prev, [editKey]: e.target.value }))}
                          className="min-h-[400px] font-body text-base leading-relaxed resize-y"
                        />
                      ) : (
                        <div id={`my-letterhead-${speech.id}`}>
                          <Letterhead
                            title={speech.speech_title || "Festtale"}
                            subtitle={`Til ${speech.recipient_name}`}
                          >
                            <SpeechText text={currentContent} forPdf={true} />
                          </Letterhead>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySpeechesPage;
